import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

// components
import Root from './Root';
import PrivateRoute from './PrivateRoute';
import MainLayout from '../components/MainLayout';

// auth
const Login = React.lazy(() => import('../pages/auth/Login'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const Confirm = React.lazy(() => import('../pages/auth/Confirm'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));

// home
const Home = React.lazy(() => import('../pages/Home'));

// landings
const App = React.lazy(() => import('../pages/landings/App'));
const Saas = React.lazy(() => import('../pages/landings/Saas/Saas'));
const Saas2 = React.lazy(() => import('../pages/landings/Saas/Saas2'));
const Startup = React.lazy(() => import('../pages/landings/Startup'));
const Software = React.lazy(() => import('../pages/landings/Software'));
const Agency = React.lazy(() => import('../pages/landings/Agency'));
const Coworking = React.lazy(() => import('../pages/landings/Coworking'));
const Crypto = React.lazy(() => import('../pages/landings/Crypto'));
const Marketing = React.lazy(() => import('../pages/landings/Marketing'));
const Portfolio = React.lazy(() => import('../pages/landings/Portfolio'));

// pages
const Dashboard = React.lazy(() => import('../pages/other/account/Dashboard'));
const Settings = React.lazy(() => import('../pages/other/account/Settings'));
const Blog = React.lazy(() => import('../pages/other/Blog'));
const BlogPost = React.lazy(() => import('../pages/other/BlogPost'));
const Company = React.lazy(() => import('../pages/other/Company'));
const Contact = React.lazy(() => import('../pages/other/Contact'));
const Career = React.lazy(() => import('../pages/other/Career'));
const Pricing = React.lazy(() => import('../pages/other/Pricing'));
const PortfolioGrid = React.lazy(() => import('../pages/other/portfolio/PortfolioGrid'));
const PortfolioMasonry = React.lazy(() => import('../pages/other/portfolio/PortfolioMasonry'));
const PortfolioItem = React.lazy(() => import('../pages/other/portfolio/PortfolioItem'));

// custom pages
const TentangPerusahaan = React.lazy(() => import('../pages/perusahaan/tentangPerusahaan'));
const Bantuan = React.lazy(() => import('../pages/perusahaan/bantuan/bantuan'));
const Karir = React.lazy(() => import('../pages/karir/Karir'));
const Sertifikasi = React.lazy(() => import('../pages/sertifikasi/sertifikasi'));
const KarirDetail = React.lazy(() => import('../pages/karirdetail/KarirDetail'));
const Keamanan = React.lazy(() => import('../pages/keamanan/keamananPrivasi'));
const Kontak = React.lazy(() => import('../pages/kontakkami/kontakKami'));
const Semuateam = React.lazy(() => import('../pages/semuateam/semuaTeam'));
const Bloghalaman = React.lazy(() => import('../pages/blog/Blog'));
const BlogInstall = React.lazy(() => import('../pages/blog/BlogInstall'));
const BlogTutorial = React.lazy(() => import('../pages/blog/BlogTutorial'));
const BlogSourceCode = React.lazy(() => import('../pages/blog/BlogSourceCode'));
const BlogTroubleshoot = React.lazy(() => import('../pages/blog/BlogTroubleshoot'));
const BlogBeritaUmum = React.lazy(() => import('../pages/blog/BlogBeritaUmum'));
const BlogAcara = React.lazy(() => import('../pages/blog/BlogAcara'));
const BlogDetail = React.lazy(() => import('../pages/blog/blogdetail/BlogDetail'));
const ProductDetail = React.lazy(() => import('../pages/Home/produkDetail'));
const Detailteam = React.lazy(() => import('../pages/semuateam/detailteam/DetailTeam'));
const NotFound = React.lazy(() => import('../pages/notfoundpage/NotFoundPage'));
const HelpDesk = React.lazy(() => import('../pages/other/HelpDesk'));

// docs
const Introduction = React.lazy(() => import('../pages/docs/Introduction'));
const QuickStart = React.lazy(() => import('../pages/docs/QuickStart'));
const Customization = React.lazy(() => import('../pages/docs/Customization'));
const Routing = React.lazy(() => import('../pages/docs/Routing'));
const CodeSpliting = React.lazy(() => import('../pages/docs/CodeSpliting'));
const ChangeLog = React.lazy(() => import('../pages/docs/ChangeLog'));
const Colors = React.lazy(() => import('../pages/docs/Colors'));
const Typography = React.lazy(() => import('../pages/docs/Typography'));
const Bootstrap = React.lazy(() => import('../pages/docs/Bootstrap'));
const Custom = React.lazy(() => import('../pages/docs/Custom'));
const Plugins = React.lazy(() => import('../pages/docs/Plugins'));
const Navbars = React.lazy(() => import('../pages/docs/Navbars'));
const Heros = React.lazy(() => import('../pages/docs/Heros'));

// global spinner
const LoadingSpinner = () => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ minHeight: "100vh" }}
  >
    <Spinner
      animation="border"
      role="status"
      variant="primary"
      style={{ width: "5rem", height: "5rem" }}
    >
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

const AllRoutes = () => {
  const routes = useRoutes([
    {
      path: '/',
      element: <Root />,
    },
    {
      path: '/',
      children: [
        {
          path: 'auth',
          children: [
            { path: 'login', element: <Login /> },
            { path: 'forget-password', element: <ForgetPassword /> },
            { path: 'confirm', element: <Confirm /> },
            { path: 'logout', element: <Logout /> },
          ],
        },
        {
          path: 'docs',
          children: [
            { path: 'introduction', element: <Introduction /> },
            { path: 'quick-start', element: <QuickStart /> },
            { path: 'customization', element: <Customization /> },
            { path: 'routing', element: <Routing /> },
            { path: 'code-spliting', element: <CodeSpliting /> },
            { path: 'change-log', element: <ChangeLog /> },
            { path: 'colors', element: <Colors /> },
            { path: 'typography', element: <Typography /> },
            { path: 'bootstrap', element: <Bootstrap /> },
            { path: 'custom', element: <Custom /> },
            { path: 'plugins', element: <Plugins /> },
            { path: 'navbars', element: <Navbars /> },
            { path: 'heros', element: <Heros /> },
          ],
        },
        {
          path: 'home',
          element: <Home />,
        },
        {
          path: 'landing',
          children: [
            { path: 'app', element: <App /> },
            { path: 'saas', element: <Saas /> },
            { path: 'saas2', element: <Saas2 /> },
            { path: 'startup', element: <Startup /> },
            { path: 'software', element: <Software /> },
            { path: 'agency', element: <Agency /> },
            { path: 'coworking', element: <Coworking /> },
            { path: 'crypto', element: <Crypto /> },
            { path: 'marketing', element: <Marketing /> },
            { path: 'portfolio', element: <Portfolio /> },
          ],
        },
        {
          path: '/',
          element: <MainLayout />,
          children: [
            { path: 'tentang', element: <TentangPerusahaan /> },
            { path: 'tentang/bantuan', element: <Bantuan /> },
            { path: 'home', element: <Home /> },
            { path: 'tentang/karir', element: <Karir /> },
            { path: 'tentang/sertifikasi', element: <Sertifikasi /> },
            { path: 'karir/:slug', element: <KarirDetail /> },
            { path: 'tentang/keamanan-privasi', element: <Keamanan /> },
            { path: 'kontak', element: <Kontak /> },
            { path: 'tentang/semua-tim', element: <Semuateam /> },
            { path: 'blog', element: <Bloghalaman /> },
            { path: 'blog/install', element: <BlogInstall /> },
            { path: 'blog/tutorial', element: <BlogTutorial /> },
            { path: 'blog/source-code', element: <BlogSourceCode /> },
            { path: 'blog/troubleshoot', element: <BlogTroubleshoot /> },
            { path: 'blog/berita-umum', element: <BlogBeritaUmum /> },
            { path: 'blog/acara', element: <BlogAcara /> },
            { path: 'blog/:id', element: <BlogDetail /> },
            { path: 'produk/:slug', element: <ProductDetail /> },
            { path: 'detailteam/:slug', element: <Detailteam /> },
            { path: '*', element: <NotFound /> },
          ],
        },
        {
          path: 'pages',
          children: [
            {
              path: 'blog',
              children: [
                { path: '', element: <Blog /> },
                { path: 'post', element: <BlogPost /> },
              ],
            },
            { path: 'company', element: <Company /> },
            { path: 'contact', element: <Contact /> },
            { path: 'career', element: <Career /> },
            { path: 'pricing', element: <Pricing /> },
            {
              path: 'portfolio',
              children: [
                { path: 'grid', element: <PortfolioGrid /> },
                { path: 'masonry', element: <PortfolioMasonry /> },
                { path: 'item', element: <PortfolioItem /> },
              ],
            },
            { path: 'help', element: <HelpDesk /> },
          ],
        },
      ],
    },
    {
      path: '/',
      element: <PrivateRoute roles={['Admin']} />,
      children: [
        {
          path: 'pages',
          children: [
            {
              path: 'account',
              children: [
                { path: 'dashboard', element: <Dashboard /> },
                { path: 'settings', element: <Settings /> },
              ],
            },
          ],
        },
      ],
    },
  ]);

  return <Suspense fallback={<LoadingSpinner />}>{routes}</Suspense>;
};

export default AllRoutes;
